// Navigationsblöcke der Sidebar — Projektion von lib/navigation.ts.
//
// Vor Paket 2 pflegte Sidebar.tsx eine handgeschriebene Liste von acht
// <NavItem>-Aufrufen (plus zwei weitere im Fussblock); "Projekte" kam darin
// doppelt vor und die Labels wichen von der MobileNav ab. Diese Datei rendert
// stattdessen NUR, was `primaryNav()` bzw. `footerNav(isAdmin)` liefert — es
// gibt hier keine Ziel-Liste, in die man ein sechstes Ziel eintragen könnte.
//
// Aktivzustand laut docs/design/KINETIC-FLOW-SYSTEM.md, Abschnitt
// "Navigation": `primary-container`-Fläche + `primary-strong`-Text + 3px
// Akzentschiene links. Die Erkennung kommt aus `isActivePath()`, damit
// Sidebar, MobileNav und Befehlspalette dieselbe Regel benutzen.

import { Link } from "wouter-preact";
import { inboxUnreadSig } from "../../lib/inboxBadge";
import { isActivePath, type NavTarget } from "../../lib/navigation";

/**
 * Löst den Zähler eines Ziels auf. `NavTarget.badge` ist ein BEZEICHNER
 * ("inbox"), kein Wert — die Zahl lebt im Signal, damit Sidebar und
 * MobileNav garantiert dieselbe sehen (lib/inboxBadge.ts).
 */
export function badgeCount(target: NavTarget): number {
  return target.badge === "inbox" ? inboxUnreadSig.value : 0;
}

export function SidebarNavItem({
  target,
  location,
  collapsed,
  close,
}: {
  target: NavTarget;
  location: string;
  collapsed: boolean;
  close: () => void;
}): preact.JSX.Element {
  const active = isActivePath(target, location);
  const badge = badgeCount(target);
  const Icon = target.icon;
  return (
    <Link
      href={target.path}
      onClick={close}
      aria-current={active ? "page" : undefined}
      class={`group relative inline-flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm transition-colors min-h-[32px] ${
        collapsed ? "justify-center" : ""
      } ${
        active
          ? "bg-[color:var(--color-primary-container)] text-[color:var(--color-primary-strong)] font-medium"
          : "text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container-low)] hover:text-[color:var(--color-on-surface)]"
      }`}
      title={target.label}
    >
      {active && !collapsed && (
        <span
          class="absolute left-0 top-1/2 h-4 -translate-y-1/2 w-[3px] rounded-r-full bg-[color:var(--color-primary)]"
          aria-hidden="true"
        />
      )}
      <Icon size={16} aria-hidden="true" class="shrink-0" />
      {!collapsed && <span class="truncate">{target.label}</span>}
      {badge > 0 && (
        <span
          class={`inline-flex min-w-[16px] h-[16px] items-center justify-center rounded-full bg-[color:var(--color-primary)] text-label font-semibold text-[color:var(--color-on-primary)] px-1 ${
            collapsed ? "absolute -top-0.5 -right-0.5" : "ml-auto"
          }`}
          aria-label={`${badge} ungelesen`}
        >
          {badge > 99 ? "99+" : badge}
        </span>
      )}
    </Link>
  );
}

/** Rendert genau die übergebenen Ziele — keine eigene Liste, kein Filter. */
export function SidebarNavList({
  targets,
  location,
  collapsed,
  close,
  ariaLabel,
  class: className,
}: {
  targets: readonly NavTarget[];
  location: string;
  collapsed: boolean;
  close: () => void;
  ariaLabel: string;
  class?: string;
}): preact.JSX.Element {
  return (
    <nav class={className} aria-label={ariaLabel}>
      {targets.map((target) => (
        <SidebarNavItem
          key={target.id}
          target={target}
          location={location}
          collapsed={collapsed}
          close={close}
        />
      ))}
    </nav>
  );
}
